import { store } from 'store';
import { setTotalDrawnGeometries } from 'store/mapSlice';

import {
  resolveInventeringIcon,
  resolvePublishedIcon,
  resolveUtkastInckickadIcon,
  resolveUtkastRedigerbarIcon
} from 'components/map/IconResolver';
import {
  activeInventeringsytaHoverStyle,
  activeInventeringsytaStyle,
  addPinRaaGray5,
  addPinRaaGray5Hover,
  drawDeletePolygonStyle,
  drawPolygonStyle,
  inventeringPinTurquoiseHoverStyle,
  inventeringPinTurquoiseStyle,
  selectedByggnadsPolygonHoverStyle,
  selectedByggnadsPolygonStyle,
  selectedByggnadsutkastYtaHoverStyle,
  selectedByggnadsutkastYtaStyle,
  templatePolygonHoverStyle
} from 'components/map/Styles';

import {
  ActiveFeatureProperties,
  Bebyggelseklass,
  BebyggelseobjektPublicerad,
  BebyggelseobjektPubliceradIndex,
  BebyggelseobjektUtkast,
  BebyggelseobjektUtkastIndex,
  BebyggelseobjektVersionHistorisk,
  ByggnadIndex,
  CentrumKoordinat,
  IconType,
  Inventering,
  SearchResultAktiviteter,
  SearchResultBebyggelseobjekt
} from 'models';

import {
  ActiveFeatureType,
  AktivitetStatusEnum,
  BebyggelseklassEnum,
  BebyggelseobjektgruppEnum,
  Layer,
  RegistreringsstatustypEnum,
  SkyddstypEnum
} from 'enums';

import { Feature, Overlay } from 'ol';
import Map from 'ol/Map';
import { Coordinate } from 'ol/coordinate';
import { getCenter, getTopLeft } from 'ol/extent';
import GeoJSON, { GeoJSONFeatureCollection } from 'ol/format/GeoJSON';
import { Circle, Geometry, MultiPolygon, Point, Polygon } from 'ol/geom';
import { fromCircle } from 'ol/geom/Polygon';
import { Group, Tile } from 'ol/layer';
import BaseLayer from 'ol/layer/Base';
import VectorLayer from 'ol/layer/Vector';
import { Pixel } from 'ol/pixel';
import { TileWMS, WMTS } from 'ol/source';
import VectorSource from 'ol/source/Vector';
import TileGrid from 'ol/tilegrid/TileGrid';
import WMTSTileGrid from 'ol/tilegrid/WMTS';

import { epsg3006Extent, lantmaterietExtent, projection, resolutions, resolutionsWmts } from './MapConfig';

import { baseHref } from 'utilities/getBaseHref';
import { getInventeringDateText } from 'utilities/getInventeringDateText';

const createEPSG3006WMSSource = ({ LAYERS, STYLES }: { LAYERS: string; STYLES: string }) => {
  return new TileWMS({
    projection,
    url: 'geo/bebyggelse/wms',
    serverType: 'geoserver',
    hidpi: false,
    tileGrid: new TileGrid({
      extent: lantmaterietExtent,
      tileSize: [256, 256],
      resolutions
    }),
    params: {
      FORMAT: 'image/png',
      VERSION: '1.1.1',
      TILED: true,
      LAYERS,
      STYLES
    }
  });
};

const createEPSG3006WMTSSource = ({
  layer,
  style,
  dimensions
}: {
  layer: string;
  style: string;
  dimensions?: { CQL_FILTER: string };
}) => {
  return new WMTS({
    projection,
    url: 'geo/bebyggelse/gwc/service/wmts',
    layer,
    matrixSet: 'EPSG:3006',
    format: 'image/png',
    tileGrid: new WMTSTileGrid({
      tileSize: 256,
      origin: getTopLeft(lantmaterietExtent),
      resolutions,
      matrixIds: resolutions.map((_, index) => 'EPSG:3006:' + index.toString()),
      extent: epsg3006Extent
    }),
    style,
    wrapX: false,
    dimensions
  });
};

const topowebWms = new Tile({
  maxResolution: resolutionsWmts[resolutionsWmts.length - 1],
  source: new TileWMS({
    url: '/lmtopowebbwms',
    params: {
      SRS: 'EPSG:3006',
      VERSION: '1.1.1',
      FORMAT: 'image/png',
      LAYERS: 'topowebbkartan',
      STYLES: '',
      TILED: true,
      ISBASELAYER: true
    },
    serverType: 'geoserver',
    projection,
    wrapX: false
  })
});
topowebWms.set('id', Layer.TOPOWEB_WMS);

const topowebWmts = new Tile({
  preload: Infinity,
  opacity: 1.0,
  extent: lantmaterietExtent,
  source: new WMTS({
    url: '/lmtopowebb/1.0.0/{layer}/{Style}/{TileMatrixSet}/{TileMatrix}/{TileRow}/{TileCol}.png',
    requestEncoding: 'REST',
    version: '1.0.0',
    layer: 'topowebb',
    matrixSet: '3006',
    format: 'image/png',
    projection,
    tileGrid: new WMTSTileGrid({
      origin: getTopLeft(lantmaterietExtent),
      resolutions: resolutionsWmts,
      matrixIds: resolutionsWmts.map((_, index) => index.toString())
    }),
    style: 'default',
    wrapX: false
  })
});
topowebWmts.set('id', Layer.TOPOWEB_WMTS);

export const ortofoto = new Tile({
  visible: false,
  source: new TileWMS({
    projection,
    serverType: 'geoserver',
    url: '/lmortofoto',
    params: {
      SRS: projection,
      VERSION: '1.1.1',
      FORMAT: 'image/png',
      LAYERS: 'Ortofoto_0.5,Ortofoto_0.4,Ortofoto_0.25,Ortofoto_0.16',
      STYLES: '',
      TILED: true,
      ISBASELAYER: false
    }
  })
});
ortofoto.set('id', Layer.ORTOFOTO);

export const wmsFastighetGranser = new Tile({
  visible: true,
  maxResolution: 16,
  source: new TileWMS({
    url: '/lmfastighet',
    projection,
    params: {
      FORMAT: 'image/png',
      VERSION: '1.1.1',
      TILED: true,
      LAYERS: 'granser,text',
      WIDTH: 256,
      HEIGHT: 256
    }
  })
});
wmsFastighetGranser.set('id', Layer.FASTIGHETSGRANSER_WMS);

export const publiceradeAktiviteterSymbolerWMTS = new Tile({
  visible: false,
  source: createEPSG3006WMTSSource({
    layer: 'bebyggelse:publicerade_aktiviteter',
    style: 'bebyggelse:publicerade_aktiviteter_symboler'
  })
});
publiceradeAktiviteterSymbolerWMTS.set('id', Layer.PUBLICERADE_AKTIVITETER_SYMBOLER_WMTS);

export const publiceradeAktiviteterYtorWMTS = new Tile({
  visible: false,
  source: createEPSG3006WMTSSource({
    layer: 'bebyggelse:publicerade_aktiviteter',
    style: 'bebyggelse:publicerade_aktiviteter_yta'
  })
});
publiceradeAktiviteterYtorWMTS.set('id', Layer.PUBLICERADE_AKTIVITETER_YTOR_WMTS);

//  WMS-lager för att hantera data vid klick i karta. Renderas ej.
export const publiceradeAktiviteterSymbolerWMS = new Tile({
  visible: false,
  source: createEPSG3006WMSSource({
    LAYERS: 'bebyggelse:publicerade_aktiviteter',
    STYLES: 'bebyggelse:publicerade_aktiviteter_symboler'
  })
});
publiceradeAktiviteterSymbolerWMS.set('id', Layer.PUBLICERADE_AKTIVITETER_SYMBOLER_WMS);

export const publiceradeBebyggelseobjektSymbolerWMTS = new Tile({
  visible: true,
  source: createEPSG3006WMTSSource({
    layer: 'bebyggelse:publicerade_bebyggelseobjekt',
    style: 'bebyggelse:publicerade_bebyggelseobjekt_symboler',
    dimensions: { CQL_FILTER: '' }
  })
});
publiceradeBebyggelseobjektSymbolerWMTS.set('id', Layer.PUBLICERADE_BEBYGGELSEOBJEKT_SYMBOLER_WMTS);

export const publiceradeBebyggelseObjektYtorWMTS = new Tile({
  visible: true,
  source: createEPSG3006WMTSSource({
    layer: 'bebyggelse:publicerade_bebyggelseobjekt',
    style: 'bebyggelse:publicerade_bebyggelseobjekt_yta'
  })
});
publiceradeBebyggelseObjektYtorWMTS.set('id', Layer.PUBLICERADE_BEBYGGELSEOBJEKT_YTOR_WMTS);

//  WMS-lager för att hantera data vid klick i karta. Renderas ej.
export const publiceradeBebyggelseObjektSymbolerWMS = new Tile({
  visible: false,
  source: createEPSG3006WMSSource({
    LAYERS: 'bebyggelse:publicerade_bebyggelseobjekt_wms_view',
    STYLES: 'bebyggelse:publicerade_bebyggelseobjekt_symboler'
  })
});
publiceradeBebyggelseObjektSymbolerWMS.set('id', Layer.PUBLICERADE_BEBYGGELSEOBJEKT_SYMBOLER_WMS);

//  WMS-lager för att hantera data vid klick i karta. Renderas ej.
export const publiceradeBebyggelseObjektYtorWMS = new Tile({
  visible: false,
  source: createEPSG3006WMSSource({
    LAYERS: 'bebyggelse:publicerade_bebyggelseobjekt_wms_view',
    STYLES: 'bebyggelse:publicerade_bebyggelseobjekt_yta'
  })
});
publiceradeBebyggelseObjektYtorWMS.set('id', Layer.PUBLICERADE_BEBYGGELSEOBJEKT_YTOR_WMS);

export const wmsSocken = new Tile({
  visible: true,
  source: new TileWMS({
    projection,
    url: 'geo/bebyggelse/wms',
    serverType: 'geoserver',
    params: {
      FORMAT: 'image/png',
      VERSION: '1.1.1',
      TILED: true,
      LAYERS: 'bebyggelse:socken',
      STYLES: 'bebyggelse:socken'
    }
  })
});
wmsSocken.set('id', Layer.SOCKEN_WMS);

export const wmsLandskap = new Tile({
  visible: true,
  source: new TileWMS({
    projection,
    url: 'geo/bebyggelse/wms',
    serverType: 'geoserver',
    params: {
      FORMAT: 'image/png',
      VERSION: '1.1.1',
      TILED: true,
      LAYERS: 'bebyggelse:landskap',
      STYLES: 'bebyggelse:landskap'
    }
  })
});
wmsLandskap.set('id', Layer.LANDSKAP_WMS);

export const terrangskuggning = new Tile({
  source: new TileWMS({
    projection,
    serverType: 'geoserver',
    url: '/lmhojdmodell',
    params: {
      SRS: 'EPSG:3006',
      VERSION: '1.1.1',
      FORMAT: 'image/png',
      LAYERS: 'terrangskuggning',
      STYLES: '',
      TILED: true,
      ISBASELAYER: true
    }
  })
});
terrangskuggning.set('id', Layer.SKUGGNING);

export const terranglutning = new Tile({
  source: new TileWMS({
    projection,
    serverType: 'geoserver',
    url: '/lmhojdmodell',
    params: {
      SRS: 'EPSG:3006',
      VERSION: '1.1.1',
      FORMAT: 'image/png',
      LAYERS: 'terranglutning',
      STYLES: '',
      TILED: true,
      ISBASELAYER: true
    }
  })
});
terranglutning.set('id', Layer.LUTNING);

export const sjokort = new Tile({
  source: new TileWMS({
    projection,
    serverType: 'carmentaserver',
    url: '/sjokort',
    params: {
      VERSION: '1.1.1',
      FORMAT: 'image/png',
      LAYERS: 'OgcWmsLayer0'
    }
  })
});
sjokort.set('id', Layer.SJOKORT);

export const topoweb = new Group({
  visible: true,
  layers: [topowebWmts, topowebWms]
});
topoweb.set('id', Layer.TOPOWEB);

export const backgroundLayers = new Group({
  layers: [ortofoto, topoweb, terranglutning, terrangskuggning, sjokort, wmsFastighetGranser]
});

export const foregroundLayers = new Group({
  layers: [
    publiceradeBebyggelseobjektSymbolerWMTS,
    publiceradeBebyggelseObjektYtorWMTS,
    publiceradeAktiviteterSymbolerWMTS,
    publiceradeAktiviteterYtorWMTS,
    wmsSocken,
    wmsLandskap
  ]
});

export const activeSource = new VectorSource();
export const activeSymbolerLayer = new VectorLayer({
  source: activeSource,
  style: (feature) => {
    const featureProperties = feature.getProperties() as ActiveFeatureProperties;
    const geometryType = feature.getGeometry()?.getType();
    const isHovered = featureProperties.id === store.getState().map.hoveredId;

    if (geometryType === 'Point') {
      if (featureProperties.type === ActiveFeatureType.UTKAST_REDIGERBAR) {
        return resolveUtkastRedigerbarIcon(featureProperties.iconType === 'PIN', isHovered);
      }
      if (featureProperties.type === ActiveFeatureType.UTKAST_INSKICKAD) {
        return resolveUtkastInckickadIcon(featureProperties.iconType === 'PIN', isHovered);
      }
      if (featureProperties.type === ActiveFeatureType.PUBLICERAD) {
        return resolvePublishedIcon(featureProperties.iconType === 'PIN', featureProperties.skyddstyp, isHovered);
      }
      if (featureProperties.type === ActiveFeatureType.INVENTERING) {
        return resolveInventeringIcon(true, isHovered);
      }
      return isHovered ? inventeringPinTurquoiseHoverStyle : inventeringPinTurquoiseStyle;
    }
  }
});
activeSymbolerLayer.set('id', Layer.ACTIVE_SYMBOLER);

export const activeYtaLayer = new VectorLayer({
  source: activeSource,
  style: (feature) => {
    const featureProperties = feature.getProperties() as ActiveFeatureProperties;
    const geometryType = feature.getGeometry()?.getType();
    const isHovered = featureProperties.id === store.getState().map.hoveredId;

    if (geometryType === 'Polygon' || geometryType === 'MultiPolygon') {
      if (featureProperties.type === ActiveFeatureType.INVENTERING) {
        return isHovered ? activeInventeringsytaHoverStyle : activeInventeringsytaStyle;
      }
      return isHovered ? selectedByggnadsutkastYtaHoverStyle : selectedByggnadsutkastYtaStyle;
    }
  }
});
activeYtaLayer.set('id', Layer.ACTIVE_YTA);

export const selectedLmByggnadsPolygonSource = new VectorSource();
export const selectedLmByggnadsPolygonLayer = new VectorLayer({
  opacity: 0.6,
  source: selectedLmByggnadsPolygonSource,
  style: (feature) => {
    if (feature.getProperties().lmRegisterbyggnadObjektidentitet === store.getState().map.hoveredId) {
      return selectedByggnadsPolygonHoverStyle;
    }
    return selectedByggnadsPolygonStyle;
  }
});
selectedLmByggnadsPolygonLayer.set('id', Layer.SELECTED_LM_BYGGNADSPOLYGON);

export const templatePolygonSource = new VectorSource();
export const templatePolygonLayer = new VectorLayer({
  source: templatePolygonSource,
  style: (feature) => {
    if (store.getState().map.hoveredId === feature.getProperties().id) {
      return templatePolygonHoverStyle;
    }
  }
});
templatePolygonLayer.set('id', Layer.TEMPLATE_POLYGON);

export const templatePointSource = new VectorSource();
export const templatePointLayer = new VectorLayer({
  source: templatePointSource,
  style: (feature) =>
    store.getState().map.hoveredId === feature.getProperties().id ? addPinRaaGray5Hover : addPinRaaGray5
});
templatePointLayer.set('id', Layer.TEMPLATE_POINT);

export const drawVectorSource = new VectorSource();
export const drawVectorLayer = new VectorLayer({
  source: drawVectorSource,
  style: drawPolygonStyle
});
drawVectorLayer.set('id', Layer.DRAW);

export const addDrawVectorEventListener = () => {
  drawVectorSource.on('change', () => store.dispatch(setTotalDrawnGeometries(drawVectorSource.getFeatures().length)));
};

export const drawDeleteVectorSource = new VectorSource();
export const drawDeleteVectorLayer = new VectorLayer({
  source: drawDeleteVectorSource,
  style: drawDeletePolygonStyle
});
drawDeleteVectorLayer.set('id', Layer.DRAW_DELETE);

export const popupOverlay = new Overlay({});

export const featureInfoPopupOverlay = new Overlay({});

export const addMapUrlToLayers = (mapUrl: string): void => {
  [topowebWms, topowebWmts, ortofoto, wmsFastighetGranser, terranglutning, terrangskuggning, sjokort]
    .map((layer) => layer.getSource())
    .forEach((source) => {
      const path = source?.getUrls();

      if (source?.getUrls()?.every((url: string) => url.startsWith(mapUrl))) return;

      if (path && path.length && mapUrl) {
        source?.setUrl(`${mapUrl}${path}`);
      } else {
        console.error('kartUrl eller path saknas', `${mapUrl}${path}`);
      }
    });

  [
    publiceradeBebyggelseobjektSymbolerWMTS,
    publiceradeBebyggelseObjektYtorWMTS,
    publiceradeAktiviteterSymbolerWMTS,
    publiceradeAktiviteterYtorWMTS,
    wmsSocken,
    wmsLandskap
  ]
    .map((layer) => layer.getSource())
    .forEach((source) => {
      const path = source?.getUrls();
      if (source?.getUrls()?.every((url: string) => url.startsWith(baseHref))) return;

      if (path && path.length && baseHref) {
        source?.setUrl(`${baseHref}${path}`);
      } else {
        console.error('kartUrl eller path saknas', `${baseHref}${path}`);
      }
    });
};

const getPolygonsAndInteriorPoints = (
  featureCollection: GeoJSONFeatureCollection,
  properties: ActiveFeatureProperties
): { polygons: Array<Feature>; points: Array<Feature> } => {
  const features = new GeoJSON().readFeatures(featureCollection);

  let polygons: Array<Feature<Geometry>> = features.filter(
    (feature: Feature) => feature.getGeometry() && feature.getGeometry()!.getType() === 'Polygon'
  );

  const multiPolygons: Array<Feature<Geometry>> = features.filter(
    (feature: Feature) => feature.getGeometry() && feature.getGeometry()!.getType() === 'MultiPolygon'
  );

  // Ser till att en inventering eller utkast med flera ytor hanteras som en multipolygon
  if (
    [ActiveFeatureType.INVENTERING, ActiveFeatureType.UTKAST_REDIGERBAR, ActiveFeatureType.UTKAST_INSKICKAD].some(
      (aft) => aft === properties.type
    )
  ) {
    if (polygons.length > 1) {
      const multiPolygon = new Feature(new MultiPolygon(polygons.map((polygon) => polygon.getGeometry() as Polygon)));
      multiPolygons.push(multiPolygon);
      polygons = [];
    }
  }

  const polygonsFeatures = [
    ...polygons,
    ...multiPolygons
      .map((f: Feature<Geometry>) => (f.getGeometry() as MultiPolygon).getPolygons())
      .flat()
      .map((polygon) => new Feature(polygon))
  ] as Array<Feature<Polygon>>;

  const pointFeatures = [...polygons, ...multiPolygons] as Array<Feature<Polygon | MultiPolygon>>;

  return {
    points: pointFeatures.map((feature: Feature<Polygon | MultiPolygon>) => {
      feature.setProperties(properties);
      const featureExtent = feature.getGeometry()!.getExtent();
      const center = getCenter(featureExtent);
      const point = new Feature(new Point(center));
      point.setProperties(properties);
      return point;
    }),
    polygons: polygonsFeatures.map((feature: Feature<Polygon>) => {
      feature.setProperties(properties);
      return feature;
    })
  };
};

const getInteriorPoint = (centrumKoordinat: CentrumKoordinat, properties: ActiveFeatureProperties): Feature => {
  const point = new Feature(new Point([centrumKoordinat.ost, centrumKoordinat.nord]));
  point.setProperties(properties);
  return point;
};

const removeFromActiveSource = (types: ActiveFeatureType[]) => {
  activeSource
    .getFeatures()
    .filter((f) => types.some((t) => t === (f.getProperties() as ActiveFeatureProperties).type))
    .forEach((f) => activeSource.removeFeature(f));
};

const isSkyddHavt = (
  bebyggelseobjekt:
    | BebyggelseobjektUtkast
    | BebyggelseobjektUtkastIndex
    | BebyggelseobjektPublicerad
    | BebyggelseobjektPubliceradIndex
    | BebyggelseobjektVersionHistorisk
) => {
  if ('sammansatt' in bebyggelseobjekt) return bebyggelseobjekt.sammansatt?.havt_skydd;
  if ('havt_skydd' in bebyggelseobjekt) return bebyggelseobjekt.havt_skydd;

  return false;
};

export const replaceInventeringInActiveSource = (options: {
  featureCollection: GeoJSONFeatureCollection;
  inventering?: Inventering;
}) => {
  removeFromActiveSource([ActiveFeatureType.INVENTERING, ActiveFeatureType.INVENTERING_PRELIMINAR]);

  if (options.featureCollection) {
    const inventeringFeatures = getPolygonsAndInteriorPoints(options.featureCollection, {
      lmRegisterbyggnadObjektidentitet: null,
      id: options.inventering?.aktivitet_id || '',
      type:
        options.inventering?.aktivitetstatus.status === AktivitetStatusEnum.PLANERAD
          ? ActiveFeatureType.INVENTERING_PRELIMINAR
          : ActiveFeatureType.INVENTERING,
      iconType: 'CIRCLE',
      title: options.inventering?.titel || 'Ny inventering',
      identitetsnummer: options.inventering?.aktivitetsnummer || '',
      description1: options.inventering
        ? getInventeringDateText(options.inventering.start_ar, options.inventering.slut_ar)
        : '',
      description2: options.inventering ? 'Inventering' : '',
      skyddstyp: undefined
    });

    // TODO Detta fungerar inte när det finns flera ytor. Fixa så att det blir en centrerad ikon för alla ytor.
    activeSource.addFeatures([...inventeringFeatures.points, ...inventeringFeatures.polygons]);
  }
};

export const replaceInventeringarInActiveSource = (aktiviteter: Array<SearchResultAktiviteter>): void => {
  removeFromActiveSource([ActiveFeatureType.INVENTERING]);

  const features = aktiviteter.map((aktivitet) => {
    return getInteriorPoint(aktivitet.centrum_koordinat, {
      lmRegisterbyggnadObjektidentitet: null,
      id: aktivitet.aktivitet_id,
      type: ActiveFeatureType.INVENTERING,
      iconType: 'CIRCLE',
      title: aktivitet.titel,
      identitetsnummer: aktivitet.aktivitetsnummer,
      description1: getInventeringDateText(aktivitet.start_ar, aktivitet.slut_ar),
      description2: 'Inventering',
      skyddstyp: undefined
    });
  });

  activeSource.addFeatures([...features]);
  activeSource.changed();
};

export const replaceBebyggelseobjektResultInActiveSource = (
  bebyggelseobjekt: Array<SearchResultBebyggelseobjekt>
): void => {
  removeFromActiveSource([ActiveFeatureType.BYGGNADINDEX]);
  const features = bebyggelseobjekt.map((bo) => {
    return getInteriorPoint(bo.centrum_koordinat, {
      lmRegisterbyggnadObjektidentitet: null,
      id: bo.id,
      type: ActiveFeatureType.PUBLICERAD,
      iconType: bo.bebyggelseklass === 'OMRADE' || bo.bebyggelseklass === 'SAMMANSATT' ? 'CIRCLE' : 'PIN',
      title: bo.titel !== bo.bebyggelsenummer ? bo.titel : 'BABA',
      identitetsnummer: bo.bebyggelsenummer,
      description1: bo.publiceringsdatum,
      description2: 'Bebyggelseobjekt',
      skyddstyp: bo.skyddstyp as SkyddstypEnum
    });
  });

  activeSource.addFeatures([...features]);
  activeSource.changed();
};

const getIconTypeByBebyggelseobjektgrupp = (grupp: BebyggelseobjektgruppEnum): IconType => {
  return grupp === 'OMRADE' || grupp === 'SAMMANSATT' ? 'CIRCLE' : 'PIN';
};

export const replaceSingleUtkastFeaturesInActiveSource = (
  featureCollection: GeoJSONFeatureCollection,
  utkast: BebyggelseobjektUtkast | BebyggelseobjektUtkastIndex
) => {
  removeFromActiveSource([ActiveFeatureType.UTKAST_REDIGERBAR, ActiveFeatureType.UTKAST_INSKICKAD]);

  if (featureCollection) {
    const features = getPolygonsAndInteriorPoints(featureCollection, {
      lmRegisterbyggnadObjektidentitet: utkast?.lm_registerbyggnad?.objektidentitet || '',
      id: utkast?.utkast_id || '',
      type:
        utkast?.registrering.registreringsstatustyp_id === RegistreringsstatustypEnum.REDIGERBAR
          ? ActiveFeatureType.UTKAST_REDIGERBAR
          : ActiveFeatureType.UTKAST_INSKICKAD,
      iconType: getIconTypeByBebyggelseobjektgrupp(
        utkast?.bebyggelseobjektgrupp || BebyggelseobjektgruppEnum.ENSKILT_BEBYGGELSEOBJEKT
      ),
      title: utkast?.titel || '',
      identitetsnummer: utkast.bebyggelsenummer,
      description1:
        ([BebyggelseobjektgruppEnum.OMRADE, BebyggelseobjektgruppEnum.SAMMANSATT].includes(
          utkast?.bebyggelseobjektgrupp
        )
          ? utkast?.ursprungligt_primar_andamal?.andamal_namn
          : utkast?.berorda_fastigheter?.join(', ')) || '',
      description2: `${utkast?.skydd?.skyddstyp_namn || ''} ${isSkyddHavt(utkast) ? ' (HÄVT)' : ''}`,
      skyddstyp: utkast?.skydd?.skyddstyp
    });

    activeSource.addFeatures([...features.points, ...features.polygons]);
  }
};

export const replaceUtkastFeaturesInActiveSource = (
  bebyggelseobjektUtkast: Array<BebyggelseobjektUtkast | BebyggelseobjektUtkastIndex> | undefined
) => {
  removeFromActiveSource([ActiveFeatureType.UTKAST_REDIGERBAR, ActiveFeatureType.UTKAST_INSKICKAD]);

  if (bebyggelseobjektUtkast && bebyggelseobjektUtkast.length) {
    const features = bebyggelseobjektUtkast.map((utkast: BebyggelseobjektUtkast | BebyggelseobjektUtkastIndex) => {
      return getPolygonsAndInteriorPoints(utkast.geometri, {
        lmRegisterbyggnadObjektidentitet: utkast.lm_registerbyggnad?.objektidentitet || '',
        id: utkast.utkast_id,
        type:
          utkast?.registrering.registreringsstatustyp_id === RegistreringsstatustypEnum.REDIGERBAR
            ? ActiveFeatureType.UTKAST_REDIGERBAR
            : ActiveFeatureType.UTKAST_INSKICKAD,
        iconType: getIconTypeByBebyggelseobjektgrupp(utkast.bebyggelseobjektgrupp),
        title: utkast.titel || '',
        identitetsnummer: utkast.bebyggelsenummer,
        description1:
          ([BebyggelseobjektgruppEnum.OMRADE, BebyggelseobjektgruppEnum.SAMMANSATT].includes(
            utkast.bebyggelseobjektgrupp
          )
            ? utkast.ursprungligt_primar_andamal?.andamal_namn
            : utkast.berorda_fastigheter?.join(', ')) || '',
        description2: `${utkast?.skydd?.skyddstyp_namn || ''} ${isSkyddHavt(utkast) ? ' (HÄVT)' : ''}`,
        skyddstyp: utkast.skydd?.skyddstyp
      });
    });

    if (!features.length) {
      throw new Error('Utkast saknar geometri');
    }

    const points = features.map((feat: { points: Array<Feature>; polygons: Array<Feature> }) => feat.points).flat();
    const polygons = features.map((feat: { points: Array<Feature>; polygons: Array<Feature> }) => feat.polygons).flat();

    activeSource.addFeatures([...points, ...polygons]);
  }

  activeSource.changed();
};

export const replaceBebyggelseobjektFeaturesInActiveSource = (
  bebyggelseobjektPublicerad:
    | Array<BebyggelseobjektPublicerad | BebyggelseobjektPubliceradIndex | BebyggelseobjektVersionHistorisk>
    | undefined
) => {
  removeFromActiveSource([ActiveFeatureType.PUBLICERAD]);

  const getIconTypeByBebyggelseklass = (klass: Bebyggelseklass): IconType => {
    return klass === 'OMRADE' || klass === 'SAMMANSATT' ? 'CIRCLE' : 'PIN';
  };

  if (bebyggelseobjektPublicerad && bebyggelseobjektPublicerad.length) {
    const features = bebyggelseobjektPublicerad.map(
      (
        bebyggelseobjekt:
          | BebyggelseobjektPublicerad
          | BebyggelseobjektPubliceradIndex
          | BebyggelseobjektVersionHistorisk
      ) =>
        getPolygonsAndInteriorPoints(bebyggelseobjekt.geometri, {
          id: bebyggelseobjekt.id,
          lmRegisterbyggnadObjektidentitet: null,
          type: ActiveFeatureType.PUBLICERAD,
          iconType: getIconTypeByBebyggelseklass(bebyggelseobjekt.bebyggelseklass),
          title: bebyggelseobjekt.titel || '',
          identitetsnummer: bebyggelseobjekt.bebyggelsenummer,
          description1:
            ([BebyggelseklassEnum.OMRADE, BebyggelseklassEnum.SAMMANSATT].includes(bebyggelseobjekt.bebyggelseklass)
              ? bebyggelseobjekt.ursprungligt_primar_andamal?.andamal_namn
              : 'berorda_fastigheter' in bebyggelseobjekt && bebyggelseobjekt.berorda_fastigheter?.join(', ')) || '',
          description2: `${bebyggelseobjekt?.skydd?.skyddstyp_namn || ''} ${
            isSkyddHavt(bebyggelseobjekt) ? ' (HÄVT)' : ''
          }`,
          skyddstyp: bebyggelseobjekt.skydd?.skyddstyp
        })
    );

    if (!features.length) {
      throw new Error('Bebyggelseobjekt saknar geometri');
    }

    const points = features.map((feat: { points: Array<Feature>; polygons: Array<Feature> }) => feat.points).flat();
    const polygons = features.map((feat: { points: Array<Feature>; polygons: Array<Feature> }) => feat.polygons).flat();

    activeSource.addFeatures([...points, ...polygons]);
  }

  activeSource.changed();
};

export const replaceFeaturesInSelectedLmByggnadSource = (byggnader: ByggnadIndex[]) => {
  selectedLmByggnadsPolygonSource.clear();

  if (byggnader.length) {
    const features = byggnader.map(({ lmbyggnad }) =>
      getPolygonsAndInteriorPoints(lmbyggnad.geometri, {
        id: null,
        lmRegisterbyggnadObjektidentitet: lmbyggnad.objektidentitet,
        type: ActiveFeatureType.BYGGNADINDEX,
        iconType: 'PIN',
        title: lmbyggnad.byggnadsnamn[0] || '',
        identitetsnummer: '',
        description1: lmbyggnad.byggnadsandamal?.map(({ andamal }) => andamal)?.join(', '),
        description2: `${lmbyggnad.husnummer}`,
        skyddstyp: undefined
      })
    );

    const polygons = features.map((feat) => feat.polygons).flat();

    selectedLmByggnadsPolygonSource.addFeatures(polygons);
    selectedLmByggnadsPolygonSource.changed();
  }
};

export const clearSelectedLmByggnadLayers = () => {
  selectedLmByggnadsPolygonSource.clear();
  selectedLmByggnadsPolygonLayer.changed();
};

export const replaceFeaturesInTemplatePolygonSource = (coordinate: Coordinate) => {
  const circle = new Circle(coordinate, 1.5);
  const feature = new Feature(fromCircle(circle, 3, 1.57079633));
  feature.setProperties({ id: 'template' });

  const omradeCircle = new Circle(coordinate, 50);
  const omradeFeature = new Feature(fromCircle(omradeCircle, 3, 1.57079633));
  omradeFeature.setProperties({ id: 'omradeTemplate' });

  templatePolygonSource.clear();
  templatePolygonSource.addFeature(feature);
  templatePolygonSource.addFeature(omradeFeature);
  templatePolygonSource.changed();
};

export const clearFeaturesInTemplateSources = () => {
  templatePointSource.clear();
  templatePointSource.changed();
  templatePolygonSource.clear();
  templatePolygonSource.changed();
};

export const getPointFromActiveLayerAtPixel = async (pixel: Pixel): Promise<Array<ActiveFeatureProperties>> => {
  let result: ActiveFeatureProperties[] = [];

  if (!activeSource.getFeatures().length) {
    return new Promise((resolve) => resolve(result));
  }

  const features = await activeSymbolerLayer
    .getFeatures(pixel)
    .catch((error) => console.error('Openlayers fail ', error));

  if (features && features.length && features[0].getGeometry()?.getType() === 'Point') {
    result = features.map((feature) => {
      const properties = feature.getProperties();
      delete properties.geometry;
      return properties;
    }) as ActiveFeatureProperties[];
  }

  return new Promise((resolve) => resolve(result));
};

export const getFeaturesAtPixelFromActiveLayer = async (
  pixel: Pixel,
  map: Map | undefined
): Promise<Array<ActiveFeatureProperties>> => {
  if (!activeSource.getFeatures().length) {
    return new Promise((resolve) => resolve([]));
  }

  const features: ActiveFeatureProperties[] = [];

  map?.forEachFeatureAtPixel(pixel, function (feature, layer) {
    if (layer.get('id') === Layer.ACTIVE_SYMBOLER && feature.getGeometry()?.getType() === 'Point') {
      const properties = feature.getProperties();
      delete properties.geometry;
      features.push(properties as ActiveFeatureProperties);
    }
  });

  return new Promise((resolve) => resolve(features));
};

export const setBackgroundLayersVisible = (visibleLayers: Layer[]) => {
  backgroundLayers.getLayers().forEach((layer: BaseLayer) => layer.setVisible(visibleLayers.includes(layer.get('id'))));
};

export const setForegroundLayersVisible = (visibleLayers: Layer[]) => {
  foregroundLayers.getLayers().forEach((layer: BaseLayer) => layer.setVisible(visibleLayers.includes(layer.get('id'))));
};

export const setApplicationLayersVisible = (layers: Layer[], map: Map | undefined) => {
  const mapLayers =
    map
      ?.getLayers()
      .getArray()
      .filter(
        (layer) =>
          !backgroundLayers
            .getLayers()
            .getArray()
            .some((bgLayer) => bgLayer === layer) &&
          !foregroundLayers
            .getLayers()
            .getArray()
            .some((fgLayer) => fgLayer === layer)
      ) || [];

  const layersToShow = mapLayers.filter((layer) => layers.some((l: string) => l === layer.get('id')));
  layersToShow.forEach((layer) => layer.setVisible(true));

  const layersToHide = mapLayers.filter((layer) => !layers.some((l: string) => l === layer.get('id')));
  layersToHide.forEach((layer) => layer.setVisible(false));
};
